/**
 * REFACTORED VERSION of useMediaPanelValues
 *
 * This file demonstrates the decomposed hook structure.
 * Once verified equivalent, this will replace useMediaPanelValues.ts
 *
 * Key improvements:
 * - Broken into 6 focused, reusable hooks + inline calculations
 * - Each hook is independently testable
 * - Explicit data flow between hooks
 * - Reduced from 718 lines to ~160 lines
 * - Returns same 23 values for backward compatibility
 */

import {MediaCardType, MediaString} from '@wandb/weave/common/types/media';
import {useMemo} from 'react';

import {ClassLabelMap} from '../../util/media';
import {RunWithHistoryAndMediaWandb} from '../MediaCard';
import {
  Layout,
  MediaBrowserPanelProps,
  MediaConfigWithDefaults,
  MediaConfigWithDefaultsAndMediaKeys,
  SizingSettings,
} from './Component';
import * as PANEL_MEDIA_BROWSER_CONFIG from './config';
import {useAllRunsWithColors} from './hooks/useAllRunsWithColors';
import {useLayoutGeneration} from './hooks/useLayoutGeneration';
import {useMediaKeys} from './hooks/useMediaKeys';
import {useMediaSliderKey} from './hooks/useMediaSliderKey';
import {useRunsWithMedia} from './hooks/useRunsWithMedia';
import {
  StepCalculationResult,
  useStepCalculation,
} from './hooks/useStepCalculation';
import {useTilePagination} from './hooks/useTilePagination';
import {TilePagination} from './tiles';
import {StepMeta} from './types';
import {useMediaCardTypes} from './useGetMediaCardTypes';
import {useConfigWithDefaults} from './useMediaPanelValues'; // Keep this for now

// Return type matches original for backward compatibility
type MediaPanelValues = {
  mediaType: MediaCardType | null;
  hasBoxes: boolean;
  hasMasks: boolean;
  layout: Layout;
  tilePagination: TilePagination | null;
  mediaKeys: string[] | undefined;
  numericKeys: string[] | undefined;
  defaultKeys: string[] | undefined;
  allMediaKeys: string[];
  keyTypes: Partial<Record<string, string>>;
  mediaString: MediaString | null;
  flattenedMediaMetadataForAllRuns: any[];
  firstMediaMetadata: any | null;
  currentStep: number;
  currentSliderValue: number;
  stepMeta: StepMeta;
  allMasks: ClassLabelMap;
  allBoxes: ClassLabelMap;
  configWithDefaults: MediaConfigWithDefaults;
  configWithDefaultsAndMediaKeys: MediaConfigWithDefaultsAndMediaKeys | null;
  sizingSettings: SizingSettings;
  snapToExistingStep: boolean;
  runsWithMedia: RunWithHistoryAndMediaWandb[];
};

/**
 * Main hook for media panel data and layout calculations.
 *
 * This is the refactored version that composes smaller, focused hooks.
 * Each hook handles one concern and can be tested independently.
 *
 * Data flow:
 * 1. useMediaKeys: Extract available media keys and types
 * 2. useAllRunsWithColors: Fetch & enrich runs with colors
 * 3. useRunsWithMedia: Filter to runs with actual media
 * 4. useStepCalculation: Calculate step ranges and current state
 * 5. useConfigWithDefaults: Build config with overlays (masks/boxes)
 * 6. useLayoutGeneration: Transform data into layout structure
 * 7. useTilePagination: Calculate pagination for large datasets
 *
 * @param props - Panel props with data, config, and queries
 * @returns All data needed to render the media panel
 */
export function useMediaPanelValues(
  props: MediaBrowserPanelProps
): MediaPanelValues {
  const {config} = props;
  const {
    mediaKeys,
    mediaSliderKey: mediaSliderKeyFromConfig,
    snapToExistingStep = true,
  } = config;

  // ============================================================================
  // STEP 1: Get available media keys and types
  // ============================================================================
  const {allMediaKeys, defaultKeys, keyTypes, numericKeys} = useMediaKeys();

  const firstMediaKey = useMemo(() => {
    return mediaKeys?.[0] ?? null;
  }, [mediaKeys]);

  const mediaString = useMemo(() => {
    return firstMediaKey != null
      ? (keyTypes[firstMediaKey] as MediaString)
      : null;
  }, [firstMediaKey, keyTypes]);

  const mediaType = useMediaCardTypes(mediaKeys)?.[0] ?? null;
  const mediaSliderKey = useMediaSliderKey(mediaSliderKeyFromConfig);

  // ============================================================================
  // STEP 2: Fetch and enrich runs with colors and metadata
  // ============================================================================
  const allRuns = useAllRunsWithColors({
    data: props.data,
    query: props.query,
    pageQuery: props.pageQuery,
    customRunColors: props.customRunColors,
  });

  // ============================================================================
  // STEP 3: Filter to runs that have media for the specified keys
  // ============================================================================
  const {runsWithMedia, runStepsMap} = useRunsWithMedia(allRuns, mediaKeys);

  // ============================================================================
  // STEP 4: Calculate step metadata and current step/slider state
  // ============================================================================
  const stepCalculation: StepCalculationResult = useStepCalculation(
    runsWithMedia,
    runStepsMap,
    mediaKeys,
    {
      mediaSliderKey,
      stepIndex: config.stepIndex,
      snapToExistingStep,
    }
  );

  const {stepMeta, currentStep, currentSliderValue, firstMediaMetadata} =
    stepCalculation;

  // ============================================================================
  // STEP 5: Build config with defaults and overlay data (masks, boxes)
  // ============================================================================
  // Note: This is kept from the original implementation as it's already extracted
  const {configWithDefaults, allMasks, hasMasks, allBoxes, hasBoxes} =
    useConfigWithDefaults({
      data: props.data,
      query: props.query,
      config: props.config,
      customRunColors: props.customRunColors,
      pageQuery: props.pageQuery,
    });

  const configWithDefaultsAndMediaKeys = useMemo(() => {
    if (mediaKeys == null) {
      return null;
    }
    return {
      ...configWithDefaults,
      mediaKeys,
    };
  }, [configWithDefaults, mediaKeys]);

  // ============================================================================
  // STEP 6: Generate layout structure (tiles, axes, etc.)
  // ============================================================================
  const layout = useLayoutGeneration({
    runsWithMedia,
    stepMeta,
    currentSliderValue,
    allMasks,
    configWithDefaults,
  });

  // ============================================================================
  // STEP 7: Calculate UI concerns (sizing, pagination)
  // ============================================================================

  // Sizing settings: Simple wrapper for column count constraints
  const sizingSettings = useMemo(() => {
    const columnCount = configWithDefaultsAndMediaKeys?.columnCount ?? 1;
    return {
      columnCount: {
        min: PANEL_MEDIA_BROWSER_CONFIG.MIN_COL_COUNT,
        max: PANEL_MEDIA_BROWSER_CONFIG.MAX_COL_COUNT,
        current: columnCount,
      },
    };
  }, [configWithDefaultsAndMediaKeys?.columnCount]);

  // Pagination: For performance with large datasets
  const tilePagination = useTilePagination(
    layout,
    configWithDefaultsAndMediaKeys,
    mediaType
  );

  // ============================================================================
  // STEP 8: Flatten media metadata for all runs (used by some components)
  // ============================================================================
  // Note: This could be extracted to its own hook if needed, but it's simple enough
  const flattenedMediaMetadataForAllRuns = useMemo(() => {
    const result: any[] = [];
    const seenKeys = new Set<string>();

    for (const r of runsWithMedia) {
      if (!r.history) {
        continue;
      }

      for (const h of r.history) {
        if (!mediaKeys) {
          continue;
        }

        for (const k of mediaKeys) {
          const metadata = h[k];
          if (metadata == null) {
            continue;
          }

          // Deduplicate based on a unique key
          const uniqueKey = `${r.name}-${h._step}-${k}`;
          if (seenKeys.has(uniqueKey)) {
            continue;
          }

          seenKeys.add(uniqueKey);
          result.push(metadata);
        }
      }
    }
    return result;
  }, [runsWithMedia, mediaKeys]);

  // ============================================================================
  // RETURN: All values (same as original for backward compatibility)
  // ============================================================================
  return {
    // Media info
    mediaType,
    mediaString,
    mediaKeys,
    allMediaKeys,
    keyTypes,
    numericKeys,
    defaultKeys,

    // Run data
    runsWithMedia,
    flattenedMediaMetadataForAllRuns,

    // Step/slider state
    stepMeta,
    currentStep,
    currentSliderValue,
    firstMediaMetadata,

    // Layout
    layout,

    // Overlays
    allMasks,
    hasMasks,
    allBoxes,
    hasBoxes,

    // Config
    configWithDefaults,
    configWithDefaultsAndMediaKeys,
    snapToExistingStep,

    // UI
    sizingSettings,
    tilePagination,
  };
}
